/** Auth-domain SSOT for role literals: `USER_ROLES` carries the canonical `UserRole` values; `CLAIM_ROLES` carries the admin user-claim API role. */

import { SENTINEL_UNKNOWN } from '@federa/const/sentinels';
import type { UserRole } from '@federa/types/auth-role';

/** Canonical `UserRole` literal map; values must stay in lockstep with the `UserRole` union in `types/auth-role.ts`. */
export const USER_ROLES = {
	USER: 'user',
	G4: 'G4',
	ADMIN: 'admin',
	SUPER_ADMIN: 'super_admin',
} as const satisfies Record<string, UserRole>;

/** Admin user-claim role literals, consumed by the POST /api/users/[id]/claim route and the user-detail action dropdown. */
export const CLAIM_ROLES = {
	SUCCESS_MANAGER: 'success_manager',
} as const;

/** Canonical query-param keys for the auth-flow forwarding logic. `APP_NAME` resolves the historical `app` vs `appName` drift -- `appName` is the single canonical key. */
export const AUTH_QUERY_PARAMS = {
	REDIRECT: 'redirect',
	APP_NAME: 'appName',
	PROPERTY_ID: 'propertyId',
	FUNCTION_CARD: 'functionCard',
	FUNCTION_ID: 'functionId',
} as const;

/** Default post-auth / post-logout redirect path, used when no `?redirect=` param is supplied and by `performLogout` after sign-out. */
export const DEFAULT_POST_AUTH_REDIRECT = '/';

/**
 * Default application name recorded when a login/auth request supplies no `appName`.
 *
 * DERIVED from {@link SENTINEL_UNKNOWN} (`const/sentinels.ts`), never re-typed -- that leaf's whole contract is "the
 * literal every consumer shares when a real value is unavailable", and an absent `app_name` is exactly that case. An
 * own `'unknown'` literal here is the shape where a re-spelling of the sentinel silently splits the two and leaves the
 * auth lane writing a value nothing else recognises. Same derive-don't-retype pattern
 * that leaf already applies to `NO_LIMIT_LOWER`. The NAME stays, because which fallback the auth lane takes is a
 * decision worth spelling; only the LITERAL was the duplicate.
 */
export const DEFAULT_APP_NAME: string = SENTINEL_UNKNOWN;
